import React from "react";
import { NotebookPageContentBlock } from "constants/notebooks/notebook-page-content-blocks";
import { BlockRecord } from "models/view-models/block-record";
import ActivePageBlock from "organisms/page-blocks/active-page-block/active-page-block";
import { AdditionalBlockData } from "models/interfaces/additional-block-data/additional-block-data";
import InactivePageBlock from "./inactive-page-block";
import SortableDragAndDropItem from "organisms/sortable-drap-and-drop/sortable-drag-and-drop-item";
import { PAGE_BLOCKS_DRAG_AND_DROP_SCOPE } from "organisms/page-blocks/page-block-list/page-block-list";
import { getBlockContent } from "utilities/get-block-content";

export interface EditablePageBlockProps {
    activateBlock: (block: BlockRecord) => void;
    activeBlock: BlockRecord | undefined;
    additionalBlockData?: AdditionalBlockData;
    block: BlockRecord;
    draggingBlockId?: number;
    isFirst: boolean;
    isLast: boolean;
    onBlockModified: (updatedBlock: BlockRecord) => void;
    onBlockSaved: (updatedBlock: BlockRecord) => void;
    onDelete: (block: BlockRecord) => Promise<void>;
    onMoveDown: (blockToMove: BlockRecord) => Promise<void>;
    onMoveUp: (blockToMove: BlockRecord) => Promise<void>;
    startNewBlock: (
        type: NotebookPageContentBlock,
        afterBlockId?: number
    ) => Promise<void>;
    onDeactivateBlock: () => void;
}

export const EditablePageBlock = ({
    activateBlock,
    activeBlock,
    additionalBlockData = {},
    block,
    draggingBlockId,
    isFirst,
    isLast,
    onBlockModified,
    onBlockSaved,
    onDelete,
    onMoveDown,
    onMoveUp,
    startNewBlock,
    onDeactivateBlock,
}: EditablePageBlockProps) => {
    const draggableData = {
        blockType: block.type,
        blockContent: getBlockContent(block, additionalBlockData),
    };

    const blockComponent =
        block.id === activeBlock?.id ? (
            <ActivePageBlock
                additionalBlockData={additionalBlockData}
                block={block}
                onBlockModified={onBlockModified}
                onBlockSaved={onBlockSaved}
                onDelete={onDelete}
                onDone={() => onDeactivateBlock()}
                onMoveDown={onMoveDown}
                onMoveUp={onMoveUp}
            />
        ) : (
            <InactivePageBlock
                block={block}
                disableMoveDown={isLast}
                disableMoveUp={isFirst}
                draggingBlockId={draggingBlockId}
                onAddBlockBelow={startNewBlock}
                onClickBlock={activateBlock}
                onDelete={onDelete}
                onMoveDown={onMoveDown}
                onMoveUp={onMoveUp}
                onBlockSaved={onBlockSaved}
                additionalBlockData={additionalBlockData}
            />
        );

    return (
        <SortableDragAndDropItem
            allowIconOnlyDrag
            dragAndDropScopeName={PAGE_BLOCKS_DRAG_AND_DROP_SCOPE}
            id={block.id!}
            key={block.id!}
            iconOnHoverOnly={true}
            draggableData={draggableData}
            enabled={activeBlock == null}>
            {blockComponent}
        </SortableDragAndDropItem>
    );
};
